import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  User, 
  DollarSign, 
  Home, 
  TrendingUp, 
  Heart,
  Save,
  X,
  Trash2,
  Edit,
  Phone,
  Plus,
  MessageSquare,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Navbar } from "../components/Navbar";
import Popup from "../components/Popup";
import ContactAgentPopup from "../components/ContactAgentPopup";
import Toast from "../components/Toast";
import { useAuth } from "../contexts/AuthContext";
import { 
  getClientInfo, 
  getUserSavedChats, 
  loadChat,
  deleteChat,
  renameChat,
  getUserFavorites,
  updateClientInfo,
  getUserPropertyReports
} from "../supabase";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientInfo, setClientInfo] = useState(null);
  const [savedChats, setSavedChats] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [propertyReports, setPropertyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatToRename, setChatToRename] = useState(null);
  const [newChatName, setNewChatName] = useState('');
  
  // Estados para popups
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showDeleteAllPopup, setShowDeleteAllPopup] = useState(false);
  const [showContactAgentPopup, setShowContactAgentPopup] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Función para mostrar toast
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const loadUserData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      
      // Cargar información del cliente
      const client = await getClientInfo(user.email);
      setClientInfo(client);
      setEditData(client || {});
      
      // Cargar chats guardados
      const chats = await getUserSavedChats(user.uid);
      setSavedChats(chats);
      
      // Cargar favoritos
      const userFavorites = await getUserFavorites(user.uid);
      setFavorites(userFavorites);
      
      // Cargar reportes PDF
      if (user.email) {
        const reports = await getUserPropertyReports(user.email);
        setPropertyReports(reports);
      }
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleEdit = () => {
    setEditData(clientInfo || {});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditData(clientInfo || {});
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!user) return;
    try {
      // Convertir strings vacíos a null para campos numéricos
      const cleanedData = { ...editData };
      const numericFields = ['income', 'savings', 'debts', 'employment_years', 'age', 'min_price', 'max_price', 'min_size', 'bedrooms', 'bathrooms'];
      numericFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        } else {
          // Asegurar que los valores numéricos sean números, no strings
          const numValue = typeof cleanedData[field] === 'string' ? parseFloat(cleanedData[field]) : cleanedData[field];
          cleanedData[field] = isNaN(numValue) ? null : numValue;
        }
      });

      await updateClientInfo(user.email, cleanedData);
      await loadUserData();
      setIsEditing(false);
      showToastMessage('Información actualizada correctamente');
    } catch (error) {
      showToastMessage('Error al actualizar la información', 'error');
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value === '' ? null : value
    }));
  };

  // Valores predefinidos para los campos de vivienda
  const minPriceOptions = [0, 50000, 75000, 100000, 125000, 150000, 175000, 200000, 225000, 250000, 275000, 300000, 325000, 350000, 375000, 400000, 425000, 450000, 475000, 500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3500000, 4000000];
  
  const maxPriceOptions = [75000, 100000, 125000, 150000, 175000, 200000, 225000, 250000, 275000, 300000, 325000, 350000, 375000, 400000, 425000, 450000, 475000, 500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3500000, 4000000];
  
  const minSizeOptions = [0, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320, 340, 360, 380, 400, 420, 440, 460, 480, 500];
  
  const bathroomsOptions = [1, 2, 3];
  
  const bedroomsOptions = [1, 2, 3, 4];

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return 'No especificado';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user, loadUserData]);

  const handleLoadChat = async (chatId) => {
    try {
      const chat = await loadChat(user.uid, chatId);
      if (chat) {
        // Navegar al chat con los mensajes cargados
        navigate('/chat', { 
          state: { 
            loadedChat: chat,
            messages: chat.messages 
          } 
        });
      }
    } catch (error) {
      showToastMessage('Error al cargar el chat', 'error');
    }
  };

  const handleDeleteChat = (chatId) => {
    setChatToDelete(chatId);
    setShowDeletePopup(true);
  };

  const confirmDeleteChat = async () => {
    try {
      await deleteChat(user.uid, chatToDelete);
      await loadUserData();
      setShowDeletePopup(false);
      setChatToDelete(null);
      showToastMessage('Chat eliminado correctamente');
    } catch (error) {
      showToastMessage('Error al eliminar el chat', 'error');
    }
  };

  const handleRenameChat = async (chatId) => {
    if (newChatName.trim()) {
      try {
        await renameChat(user.uid, chatId, newChatName.trim());
        await loadUserData();
        setChatToRename(null);
        setNewChatName('');
        showToastMessage('Chat renombrado correctamente');
      } catch (error) {
        showToastMessage('Error al renombrar el chat', 'error');
      }
    }
  };

  const handleDeleteAllChats = () => {
    setShowDeleteAllPopup(true);
  };

  const confirmDeleteAllChats = async () => {
    try {
      // Eliminar todos los chats uno por uno
      for (const chat of savedChats) {
        await deleteChat(user.uid, chat.id);
      }
      await loadUserData();
      setShowDeleteAllPopup(false);
      showToastMessage('Todos los chats han sido eliminados');
    } catch (error) {
      showToastMessage('Error al eliminar los chats', 'error');
    }
  };


  const getHousingScore = () => {
    if (!clientInfo) return 0;
    return clientInfo.housing_readiness_score || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  const housingScore = getHousingScore();

  return (
    <div className="min-h-screen flex bg-background">
      <Navbar />
      
      {/* Overlay para móvil cuando el menú está abierto */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar izquierdo - Chats (estilo ChatGPT) */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
        flex flex-col w-64 lg:w-64 md:w-80 bg-card border-r border-border flex-shrink-0 h-screen
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:flex
      `}>
        <div className="flex-1 flex flex-col overflow-hidden pt-16">
          {/* Header del sidebar con icono de chat y título en móvil */}
          <div className="p-2 border-b border-border">
            {/* Título visible en móvil */}
            <div className="lg:hidden mb-2 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold truncate">Mis Chats</h2>
                <p className="text-xs text-muted-foreground truncate">Conversaciones guardadas</p>
              </div>
              <Button
                onClick={() => setIsMobileMenuOpen(false)}
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Botón nuevo chat */}
            <Button
              onClick={() => {
                navigate('/chat');
                setIsMobileMenuOpen(false);
              }}
              variant="hero"
              className="w-full justify-start gap-2 h-9 text-sm"
            >
              <Plus className="w-4 h-4" />
              Nuevo chat
            </Button>
          </div>

          {/* Lista de Chats con estilo de conversaciones */}
          <div className="flex-1 overflow-y-auto p-1.5">
            {savedChats.length === 0 ? (
              <div className="text-center py-6 px-3">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-primary/50" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">No hay chats guardados</p>
                <p className="text-xs text-muted-foreground">
                  Tus conversaciones con GABI aparecerán aquí
                </p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {savedChats.map((chat) => (
                  <div
                    key={chat.id}
                    className="group relative flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    {chatToRename === chat.id ? (
                      <div className="flex-1 flex flex-col gap-1.5">
                        <Input
                          value={newChatName}
                          onChange={(e) => setNewChatName(e.target.value)}
                          placeholder="Nuevo nombre"
                          className="text-sm h-8"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <Button
                            onClick={() => handleRenameChat(chat.id)}
                            size="sm"
                            variant="hero"
                            className="flex-1 h-7 text-xs"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            onClick={() => {
                              setChatToRename(null);
                              setNewChatName('');
                            }}
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Icono de chat */}
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-4 h-4 text-primary" />
                        </div>
                        
                        {/* Contenido del chat */}
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => {
                            handleLoadChat(chat.id);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold truncate text-foreground">{chat.chat_name}</p>
                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                            {chat.updated_at ? new Date(chat.updated_at).toLocaleDateString('es-ES', { 
                              day: 'numeric', 
                              month: 'short'
                            }) : 'Sin fecha'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Botones de acción - siempre visibles */}
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setChatToRename(chat.id);
                              setNewChatName(chat.chat_name);
                            }}
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 hover:bg-secondary/50"
                            title="Renombrar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteChat(chat.id);
                            }}
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-500/10"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer del sidebar */}
          <div className="p-2 border-t border-border">
            {savedChats.length > 0 && (
              <Button
                onClick={handleDeleteAllChats}
                variant="ghost"
                size="sm"
                className="w-full text-red-500 hover:text-red-700 hover:bg-red-500/10 h-8 text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Eliminar todos los chats
              </Button>
            )}
          </div>
        </div>
      </aside>

      {/* Contenido Principal */}
      <main className="flex-1 overflow-hidden pt-20 h-screen">
        <div className="h-full px-4 sm:px-6 lg:px-8 pb-4 overflow-y-auto">
          {/* Header */}
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Botón para abrir menú en móvil */}
              <Button
                onClick={() => setIsMobileMenuOpen(true)}
                variant="ghost"
                size="icon"
                className="lg:hidden"
              >
                <MessageSquare className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Dashboard</h1>
                <p className="text-sm text-muted-foreground">Tu información financiera y preferencias</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    variant="hero"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleEdit}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </Button>
            <Button
              onClick={() => setShowContactAgentPopup(true)}
              variant="hero"
              size="sm"
              className="flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Contactar
            </Button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {/* Grid: Puntuación, Favoritos y Reportes */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Puntuación de Preparación */}
              <div className="bg-card rounded-xl p-4 shadow-lg">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4" />
                  Puntuación de Preparación Financiera
                </h2>
                
                <div className="flex items-center gap-3">
                  <div className="relative w-20 h-20 flex-shrink-0">
                    <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-secondary"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(housingScore / 100) * 251.2} 251.2`}
                        className={`transition-all duration-1000 ${
                          housingScore >= 80 ? 'text-green-500' :
                          housingScore >= 60 ? 'text-yellow-500' :
                          housingScore >= 40 ? 'text-orange-500' : 'text-red-500'
                        }`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold">{housingScore}</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold mb-1">
                      {housingScore >= 80 ? 'Excelente preparación' :
                       housingScore >= 60 ? 'Buena preparación' :
                       housingScore >= 40 ? 'Preparación moderada' : 'Necesita mejorar'}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {housingScore >= 80 
                        ? 'Tienes una excelente preparación financiera para la compra de vivienda.'
                        : housingScore >= 60
                        ? 'Tienes una buena base financiera. Con algunos ajustes podrás mejorar tu posición.'
                        : housingScore >= 40
                        ? 'Tu preparación es moderada. Te recomendamos trabajar en mejorar tus finanzas.'
                        : 'Tu preparación financiera necesita mejorar. Consulta con un analista.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Propiedades Favoritas */}
              <div className="bg-card rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Propiedades Favoritas
                  </h2>
                </div>
                
                <div className="flex items-center gap-3">
                  <Heart className="w-12 h-12 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold mb-1">
                      {favorites.length} propiedades favoritas
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {favorites.length === 0 
                        ? 'Las propiedades que marques como favoritas aparecerán aquí'
                        : 'Ve todas tus propiedades favoritas en detalle'}
                    </p>
                    <Button
                      onClick={() => navigate('/favorites')}
                      variant="hero"
                      size="sm"
                      className="w-full"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Ver favoritas
                    </Button>
                  </div>
                </div>
              </div>

              {/* Reportes PDF */}
              <div className="bg-card rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Informes Generados
                  </h2>
                </div>
                
                <div className="flex items-center gap-3">
                  <FileText className="w-12 h-12 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold mb-1">
                      {propertyReports.length} informe{propertyReports.length !== 1 ? 's' : ''}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {propertyReports.length === 0 
                        ? 'Los informes PDF que generes aparecerán aquí'
                        : 'Accede a todos tus informes de propiedades'}
                    </p>
                    {propertyReports.length > 0 ? (
                      <Button
                        onClick={() => navigate('/reports')}
                        variant="hero"
                        size="sm"
                        className="w-full"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Ver informes
                      </Button>
                    ) : (
                      <Button
                        onClick={() => navigate('/analyzer')}
                        variant="hero"
                        size="sm"
                        className="w-full"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Generar informe
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Información Financiera */}
            <div className="bg-card rounded-xl p-4 shadow-lg">
              <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4" />
                Información Financiera
              </h2>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Ingresos Mensuales</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editData?.income || ''}
                      onChange={(e) => handleInputChange('income', e.target.value)}
                      placeholder="0"
                      className="text-center text-lg font-bold"
                    />
                  ) : (
                  <p className="text-lg font-bold text-primary">
                    {formatCurrency(clientInfo?.income)}
                  </p>
                  )}
                </div>
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Ahorros</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editData?.savings || ''}
                      onChange={(e) => handleInputChange('savings', e.target.value)}
                      placeholder="0"
                      className="text-center text-lg font-bold"
                    />
                  ) : (
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(clientInfo?.savings)}
                  </p>
                  )}
                </div>
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Deudas</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editData?.debts || ''}
                      onChange={(e) => handleInputChange('debts', e.target.value)}
                      placeholder="0"
                      className="text-center text-lg font-bold"
                    />
                  ) : (
                  <p className="text-lg font-bold text-red-600">
                    {formatCurrency(clientInfo?.debts)}
                  </p>
                  )}
                </div>
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Años Trabajando</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editData?.employment_years || ''}
                      onChange={(e) => handleInputChange('employment_years', e.target.value)}
                      placeholder="0"
                      className="text-center text-lg font-bold"
                    />
                  ) : (
                  <p className="text-lg font-bold text-blue-600">
                    {clientInfo?.employment_years !== null && clientInfo?.employment_years !== undefined ? `${clientInfo.employment_years} años` : 'No especificado'}
                  </p>
                  )}
                </div>
              </div>
            </div>

            {/* Grid de 2 columnas: Perfil y Preferencias */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Perfil Personal */}
              <div className="bg-card rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Perfil Personal
                  </h2>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Nombre</p>
                    {isEditing ? (
                      <Input
                        value={editData?.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Nombre"
                        className="text-sm"
                      />
                    ) : (
                    <p className="text-sm font-medium">{clientInfo?.name || 'No especificado'}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Email</p>
                    <p className="text-sm font-medium truncate">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Teléfono</p>
                    {isEditing ? (
                      <Input
                        value={editData?.phone_number || ''}
                        onChange={(e) => handleInputChange('phone_number', e.target.value)}
                        placeholder="Teléfono"
                        className="text-sm"
                      />
                    ) : (
                    <p className="text-sm font-medium">{clientInfo?.phone_number || 'No especificado'}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Edad</p>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editData?.age || ''}
                        onChange={(e) => handleInputChange('age', e.target.value)}
                        placeholder="Edad"
                        className="text-sm"
                      />
                    ) : (
                    <p className="text-sm font-medium">{clientInfo?.age || 'No especificado'}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Tipo de empleo</p>
                    {isEditing ? (
                      <Select
                        value={editData?.employment_type || ''}
                        onChange={(val) => handleInputChange('employment_type', val)}
                        options={[
                          { value: '', label: 'No especificado' },
                          { value: 'funcionario', label: 'Funcionario' },
                          { value: 'indefinido', label: 'Indefinido' },
                          { value: 'temporal', label: 'Temporal' },
                          { value: 'autónomo', label: 'Autónomo' }
                        ]}
                        placeholder="Seleccionar tipo de empleo"
                      />
                    ) : (
                    <p className="text-sm font-medium">{clientInfo?.employment_type || 'No especificado'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preferencias de Vivienda */}
              <div className="bg-card rounded-xl p-4 shadow-lg">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                  <Home className="w-4 h-4" />
                  Preferencias de Vivienda
                </h2>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Ubicación</p>
                    <p className="text-sm font-bold text-primary truncate">
                      {clientInfo?.location || 'No especificado'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Precio Mínimo</p>
                    {isEditing ? (
                      <select
                        value={editData?.min_price || ''}
                        onChange={(e) => handleInputChange('min_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                        className="w-full text-sm md:text-base text-center font-bold bg-card border-2 border-primary/20 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all hover:border-primary/40 appearance-none cursor-pointer min-h-[44px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236C4DF5' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="">No especificado</option>
                        {minPriceOptions.map(price => (
                          <option key={price} value={price}>
                            {formatCurrency(price)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm md:text-base font-bold text-primary">
                      {formatCurrency(clientInfo?.min_price)}
                    </p>
                    )}
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Precio Máximo</p>
                    {isEditing ? (
                      <select
                        value={editData?.max_price || ''}
                        onChange={(e) => handleInputChange('max_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                        className="w-full text-sm md:text-base text-center font-bold bg-card border-2 border-primary/20 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all hover:border-primary/40 appearance-none cursor-pointer min-h-[44px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236C4DF5' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="">No especificado</option>
                        {maxPriceOptions.map(price => (
                          <option key={price} value={price}>
                            {formatCurrency(price)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm md:text-base font-bold text-primary">
                      {formatCurrency(clientInfo?.max_price)}
                    </p>
                    )}
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Tamaño Mínimo (m²)</p>
                    {isEditing ? (
                      <select
                        value={editData?.min_size || ''}
                        onChange={(e) => handleInputChange('min_size', e.target.value === '' ? null : parseFloat(e.target.value))}
                        className="w-full text-sm md:text-base text-center font-bold bg-card border-2 border-primary/20 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all hover:border-primary/40 appearance-none cursor-pointer min-h-[44px]"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236C4DF5' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="">No especificado</option>
                        {minSizeOptions.map(size => (
                          <option key={size} value={size}>
                            {size} m²
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm md:text-base font-bold text-blue-600">
                      {clientInfo?.min_size !== null && clientInfo?.min_size !== undefined ? `${clientInfo.min_size} m²` : 'No especificado'}
                    </p>
                    )}
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Habitaciones</p>
                    {isEditing ? (
                      <Select
                        value={editData?.bedrooms || ''}
                        onChange={(val) => handleInputChange('bedrooms', val === null ? null : parseFloat(val))}
                        options={[
                          { value: '', label: 'No especificado' },
                          ...bedroomsOptions.map(bedrooms => ({ value: bedrooms, label: String(bedrooms) }))
                        ]}
                        placeholder="No especificado"
                        forceTop={true}
                      />
                    ) : (
                      <p className="text-sm md:text-base font-bold text-purple-600">
                      {clientInfo?.bedrooms !== null && clientInfo?.bedrooms !== undefined ? clientInfo.bedrooms : 'No especificado'}
                    </p>
                    )}
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Baños</p>
                    {isEditing ? (
                      <Select
                        value={editData?.bathrooms || ''}
                        onChange={(val) => handleInputChange('bathrooms', val === null ? null : parseFloat(val))}
                        options={[
                          { value: '', label: 'No especificado' },
                          ...bathroomsOptions.map(bathrooms => ({ value: bathrooms, label: String(bathrooms) }))
                        ]}
                        placeholder="No especificado"
                        forceTop={true}
                      />
                    ) : (
                      <p className="text-sm md:text-base font-bold text-green-600">
                      {clientInfo?.bathrooms !== null && clientInfo?.bathrooms !== undefined ? clientInfo.bathrooms : 'No especificado'}
                    </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
      
      {/* Popups */}
      <Popup
        isOpen={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onConfirm={confirmDeleteChat}
        title="Eliminar Chat"
        message="¿Estás seguro de que quieres eliminar este chat? Esta acción no se puede deshacer."
        type="confirm"
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
      
      <Popup
        isOpen={showDeleteAllPopup}
        onClose={() => setShowDeleteAllPopup(false)}
        onConfirm={confirmDeleteAllChats}
        title="Eliminar Todos los Chats"
        message="¿Estás seguro de que quieres eliminar TODOS los chats? Esta acción no se puede deshacer."
        type="confirm"
        confirmText="Eliminar Todos"
        cancelText="Cancelar"
      />
      
      {/* Popup de contacto con agente */}
      <ContactAgentPopup
        isOpen={showContactAgentPopup}
        onClose={() => setShowContactAgentPopup(false)}
        userEmail={user?.email}
        userName={clientInfo?.name}
      />
      
      {/* Toast de notificación */}
      <Toast
        isVisible={showToast}
        message={toastMessage}
        type={toastType}
        duration={3000}
      />
    </div>
  );
};

export default Dashboard;
